import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { format } from 'date-fns';
import { orderService } from '../services/order.service';
import OptimizationRunDetailsDialog from './OptimizationRunDetailsDialog';

const statusColors = {
  DRAFT: 'default',
  SENT: 'primary',
  ACKNOWLEDGED: 'info',
  SHIPPED: 'secondary',
  RECEIVED: 'success',
  CANCELLED: 'error'
};

const PODetailsDialog = ({ open, onClose, poNumber }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [poDetails, setPODetails] = useState(null);
  
  // Add state for optimization run dialog
  const [optRunDialogOpen, setOptRunDialogOpen] = useState(false);
  const [selectedOptRunId, setSelectedOptRunId] = useState(null);

  useEffect(() => {
    if (open && poNumber) {
      fetchPODetails();
    }
  }, [open, poNumber]);

  const fetchPODetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await orderService.getPODetails(poNumber);
      
      if (response.status === 'success') {
        setPODetails(response.data);
      } else {
        setError(response.message || 'Failed to fetch PO details');
      }
    } catch (error) {
      console.error('Error fetching PO details:', error);
      setError('An error occurred while fetching PO details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };
  
  // Function to handle opening the optimization run dialog
  const handleOpenOptRunDialog = (runId) => {
    setSelectedOptRunId(runId);
    setOptRunDialogOpen(true);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">Purchase Order Details</Typography>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : poDetails ? (
            <Box>
              {/* Header Section */}
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">
                        PO #{poDetails.header.po_number}
                      </Typography>
                      <Chip 
                        label={poDetails.header.status} 
                        color={statusColors[poDetails.header.status] || 'default'} 
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Store</Typography>
                    <Typography variant="body1" gutterBottom>
                      {poDetails.header.store_name} ({poDetails.header.store_id})
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">Supplier</Typography>
                    <Typography variant="body1" gutterBottom>
                      {poDetails.header.supplier_name} ({poDetails.header.supplier_id})
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">Order Date</Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDate(poDetails.header.order_date)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="body1" gutterBottom>
                      ${poDetails.header.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">Items Ordered</Typography>
                    <Typography variant="body1" gutterBottom>
                      {poDetails.header.total_items_ordered?.toLocaleString() || 'N/A'}
                    </Typography>
                    
                    <Typography variant="subtitle2" color="text.secondary">Optimization Run</Typography>
                    <Typography variant="body1" gutterBottom>
                      {poDetails.header.optimization_run_id ? (
                        <Link
                          component="button"
                          variant="body1"
                          onClick={() => handleOpenOptRunDialog(poDetails.header.optimization_run_id)}
                          sx={{ 
                            textDecoration: 'underline', 
                            cursor: 'pointer',
                            color: 'primary.main'
                          }}
                        >
                          {poDetails.header.optimization_run_id}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">First Acknowledgment</Typography>
                        <Typography variant="body2">
                          {formatDate(poDetails.header.first_acknowledgment_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">Last Shipment</Typography>
                        <Typography variant="body2">
                          {formatDate(poDetails.header.last_shipment_date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" color="text.secondary">Last Receipt</Typography>
                        <Typography variant="body2">
                          {formatDate(poDetails.header.last_receipt_date)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Line Items Section */}
              <Typography variant="h6" gutterBottom>Line Items</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line #</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poDetails.lines.map((line) => (
                      <TableRow key={line.line_number}>
                        <TableCell>{line.line_number}</TableCell>
                        <TableCell>{line.sku}</TableCell>
                        <TableCell>{line.category}</TableCell>
                        <TableCell align="right">{line.quantity.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          ${line.unit_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell align="right">
                          ${line.total_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={line.status} 
                            size="small"
                            color={statusColors[line.status] || 'default'} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="info">No purchase order data available</Alert>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add the Optimization Run Details Dialog */}
      <OptimizationRunDetailsDialog
        open={optRunDialogOpen}
        onClose={() => setOptRunDialogOpen(false)}
        runId={selectedOptRunId}
      />
    </>
  );
};

export default PODetailsDialog; 